
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BarChart, 
  Activity, 
  Download, 
  Shield, 
  Database,
  TrendingUp,
  Users,
  Clock
} from 'lucide-react';
import AnalyticsDashboard from '../AnalyticsDashboard';
import EnhancedAnalyticsDashboard from '../EnhancedAnalyticsDashboard';
import SystemHealthMonitor from '../SystemHealthMonitor';
import DataExportManager from './DataExportManager';
import RealTimeMetrics from './RealTimeMetrics';
import AuditTrailsManager from './AuditTrailsManager';
import { usePlatformStats } from '@/hooks/useAnalytics';

const IntegratedAnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { data: platformStats } = usePlatformStats();

  const quickStats = [
    {
      label: 'Total Users',
      value: platformStats?.total_users || 0,
      icon: Users,
      trend: '+12%',
      color: 'text-black'
    },
    {
      label: 'Active Sessions',
      value: platformStats?.active_users_today || 0,
      icon: Activity,
      trend: '+5%',
      color: 'text-green-600'
    },
    {
      label: 'Avg Response Time',
      value: '145ms',
      icon: Clock,
      trend: '-8%',
      color: 'text-black'
    },
    {
      label: 'System Health',
      value: '98.5%',
      icon: Database,
      trend: '+0.2%',
      color: 'text-emerald-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Integrated Analytics Dashboard</h2>
          <p className="text-black">
            Comprehensive platform analytics, system health, and audit trails
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Quick Export
          </Button>
          <Badge variant="outline" className="text-green-600 border-green-200">
            System Healthy
          </Badge>
        </div>
      </div>

      {/* Quick Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {quickStats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-black">{stat.label}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className={`text-xs ${stat.color}`}>{stat.trend} from last week</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="enhanced">Enhanced</TabsTrigger>
          <TabsTrigger value="realtime">Real-Time</TabsTrigger>
          <TabsTrigger value="health">System Health</TabsTrigger>
          <TabsTrigger value="exports">Data Export</TabsTrigger>
          <TabsTrigger value="audit">Audit Trails</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart className="h-5 w-5" />
                Platform Analytics Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AnalyticsDashboard />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enhanced">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Enhanced Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <EnhancedAnalyticsDashboard />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="realtime">
          <RealTimeMetrics />
        </TabsContent>

        <TabsContent value="health">
          <SystemHealthMonitor />
        </TabsContent>

        <TabsContent value="exports">
          <DataExportManager />
        </TabsContent>

        <TabsContent value="audit">
          <AuditTrailsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegratedAnalyticsDashboard;
